import React, { useMemo } from "react";
import { Edges } from "@react-three/drei";
import { Shape, Path, ExtrudeGeometry } from "three";
import {
  BORDO,
  SPESSORE_MURO,
  PORTINA_LAT_LARGHEZZA,
  PORTINA_LAT_ALTEZZA,
  PORTINA_LAT_GAP,
  C_MURO,
  C_BORDO_SCURO,
} from "./constants";

/**
 * Estensione dello squarcio oltre il foro murario, in metri e con origine nello
 * spigolo basso-sinistro del foro. Serve a scoprire il controtelaio in luce, che
 * ha sezione fissa (100 mm) e quindi sborda quando lo sguincio e' piu' stretto:
 * senza allargare lo squarcio i tubolari resterebbero annegati nella muratura.
 */
export type Squarcio = { xMin: number; xMax: number; yMax: number };

export const MuroConSquarcio: React.FC<{
  larghezza: number;
  altezza: number;
  showPortinaLaterale?: boolean;
  squarcio?: Squarcio;
  /**
   * Distanza reale (metri) fra il foro e ciascuna parete laterale/il soffitto,
   * la stessa passata a GarageWalls: il contorno esterno di questo muro deve
   * coincidere con la faccia interna delle pareti laterali, altrimenti a
   * spalletta 0 resterebbe uno squarcio vuoto invece di un muro che si sposta
   * fino al bordo del foro. Default BORDO per compatibilita'.
   */
  margineSx?: number;
  margineDx?: number;
  margineTop?: number;
}> = ({ larghezza, altezza, showPortinaLaterale, squarcio, margineSx, margineDx, margineTop }) => {
  const geometry = useMemo(() => {
    // Il contorno esterno e' a filo delle pareti laterali del garage (stesso
    // margine passato a GarageWalls): spostarlo scollerebbe i muri.
    const leftEdge = -(margineSx ?? BORDO);
    const rightEdge = larghezza + (showPortinaLaterale ? PORTINA_LAT_GAP + PORTINA_LAT_LARGHEZZA : 0) + (margineDx ?? BORDO);
    const topEdge = altezza + (margineTop ?? BORDO);

    // Lo squarcio si allarga fino all'ingombro dei tubolari, restando dentro il
    // contorno: il margine e' normalmente ben piu' largo degli sbordi di
    // 100-200 mm dei tubolari, ma il clamp evita comunque una geometria
    // degenere se le misure cambiassero.
    const holeMinX = Math.max(Math.min(squarcio?.xMin ?? 0, 0), leftEdge);
    const holeMaxX = Math.min(Math.max(squarcio?.xMax ?? larghezza, larghezza), rightEdge);
    const holeMaxY = Math.min(Math.max(squarcio?.yMax ?? altezza, altezza), topEdge);

    const shape = new Shape();
    shape.moveTo(leftEdge, 0);
    shape.lineTo(rightEdge, 0);
    shape.lineTo(rightEdge, topEdge);
    shape.lineTo(leftEdge, topEdge);
    shape.lineTo(leftEdge, 0);

    const hole = new Path();
    hole.moveTo(holeMinX, 0);
    hole.lineTo(holeMaxX, 0);
    hole.lineTo(holeMaxX, holeMaxY);
    hole.lineTo(holeMinX, holeMaxY);
    hole.lineTo(holeMinX, 0);
    shape.holes.push(hole);

    if (showPortinaLaterale) {
      const pxLeft  = larghezza + PORTINA_LAT_GAP;
      const pxRight = pxLeft + PORTINA_LAT_LARGHEZZA;
      const portinaHole = new Path();
      portinaHole.moveTo(pxLeft, 0);
      portinaHole.lineTo(pxRight, 0);
      portinaHole.lineTo(pxRight, PORTINA_LAT_ALTEZZA);
      portinaHole.lineTo(pxLeft, PORTINA_LAT_ALTEZZA);
      portinaHole.lineTo(pxLeft, 0);
      shape.holes.push(portinaHole);
    }

    return new ExtrudeGeometry(shape, { depth: SPESSORE_MURO, bevelEnabled: false });
  }, [
    larghezza, altezza, showPortinaLaterale,
    squarcio?.xMin, squarcio?.xMax, squarcio?.yMax,
    margineSx, margineDx, margineTop,
  ]);

  return (
    <mesh geometry={geometry} position={[0, 0, -SPESSORE_MURO / 2 + 0.01]}>
      <meshStandardMaterial color={C_MURO} />
      <Edges color={C_BORDO_SCURO} />
    </mesh>
  );
};
